import styled, { css, DefaultTheme } from 'styled-components/macro';
import { ClearButtonProps, ContainerProps, SizeProps } from './input.types';
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg';
import { fieldChromeCss, FieldSize, sizeConfig } from '../_core/field';

// ─── Border radius helpers ────────────────────────────────────────────────────

const borderRadiusForAddon = (
  radius: string,
  hasAddonBefore: boolean,
  hasAddonAfter: boolean
) => {
  if (hasAddonBefore && hasAddonAfter) {
    return '0';
  }

  if (hasAddonBefore) {
    return `0 ${radius} ${radius} 0`;
  }

  if (hasAddonAfter) {
    return `${radius} 0 0 ${radius}`;
  }

  return radius;
};

// ─── AffixWrapper ─────────────────────────────────────────────────────────────

export const AffixWrapper = styled.span<ContainerProps>`
  ${({ theme, $size, $status, $hasAddonBefore, $hasAddonAfter }) => {
    const { height, paddingInline, borderRadius, fontSize, lineHeight } =
      sizeConfig(theme)[$size];

    return css`
      ${fieldChromeCss(theme, $status)}

      box-sizing: border-box;
      padding-inline: ${paddingInline};
      align-items: center;
      gap: ${theme.spacings.xs};
      width: 100%;
      height: ${height};
      border-radius: ${borderRadiusForAddon(
        borderRadius,
        $hasAddonBefore,
        $hasAddonAfter
      )};
      font-size: ${fontSize};
      line-height: ${lineHeight};
    `;
  }}
`;

// ─── Input elements ───────────────────────────────────────────────────────────

const baseInputCss = (theme: DefaultTheme, size: FieldSize) => {
  const { fontSize, lineHeight } = sizeConfig(theme)[size];

  return css`
    font-family: inherit;
    font-size: ${fontSize};
    line-height: ${lineHeight};
    color: ${theme.semantic.text.default};
    background-color: transparent;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
      color: ${theme.semantic.text.placeholder};
    }

    &:disabled {
      color: ${theme.semantic.text.disabled};
      -webkit-text-fill-color: ${theme.semantic.text.disabled};
      /* Blink и WebKit не применяют cursor к выключенному контролю, поэтому
         пропускаем наведение на враппер — курсор рисует он */
      pointer-events: none;
    }

    /* Убираем стрелки у number-инпута */
    appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &::-webkit-search-cancel-button {
      appearance: none;
    }
  `;
};

export const InputEl = styled.input<SizeProps>`
  ${({ theme, $size }) => css`
    ${baseInputCss(theme, $size)}

    flex: 1;
    min-width: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    transition: color ${theme.tokens.animation.duration.base};
  `}
`;

// ─── Prefix / Suffix ──────────────────────────────────────────────────────────

export const Prefix = styled.span`
  display: flex;
  flex: none;
  align-items: center;
  user-select: none;
`;

export const Suffix = styled.span`
  display: flex;
  flex: none;
  align-items: center;
  gap: ${({ theme }) => theme.spacings.xs};
  user-select: none;
`;

// ─── Clear button ─────────────────────────────────────────────────────────────

export const ClearButton = styled.button<ClearButtonProps>`
  ${({ theme, $visible }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${theme.semantic.field.icon};
    width: ${$visible ? '16px' : '0'};
    /* Скрытая кнопка гасит и свой gap из Suffix, иначе справа остаётся дыра */
    margin-inline-start: ${$visible ? '0' : `calc(-1 * ${theme.spacings.xs})`};
    overflow: hidden;
    opacity: ${$visible ? 1 : 0};
    pointer-events: ${$visible ? 'auto' : 'none'};

    transition: color ${theme.tokens.animation.duration.base};

    &:hover {
      color: ${theme.semantic.field.iconHover};
    }

    svg {
      display: block;
      min-width: 16px;
      height: 16px;
    }

    ${AffixWrapper}:focus-within & {
      transition-property: width, margin-inline-start, opacity, color;
      transition-timing-function: ${theme.tokens.animation.easing.standard};
      transition-duration: ${theme.tokens.animation.duration.base};
    }
  `}
`;

export { CrossIcon };

// ─── Addon group ──────────────────────────────────────────────────────────────

export const GroupWrapper = styled.span`
  display: inline-flex;
  width: 100%;
`;

export const Group = styled.span`
  display: flex;
  align-items: stretch;
  width: 100%;

  /*
   * Любой первый/последний ребёнок (Addon, Button, Select и т.д.) теряет
   * border-radius на стороне, прилегающей к инпуту, и подтягивается на пиксель:
   * иначе его рамка встаёт вплотную к рамке инпута и шов выходит вдвое толще
   * внешних границ.
   */
  > *:first-child:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    margin-inline-end: -1px;
  }

  > *:last-child:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-inline-start: -1px;
  }

  /*
   * Соседи наезжают друг на друга на пиксель, поэтому на шве видна рамка того,
   * кто нарисован позже: слева — инпута, справа — аддона. Фокус должен
   * побеждать с обеих сторон, поэтому поднимаем сфокусированного ребёнка над
   * соседями. Работает одинаково для голого инпута, для AffixWrapper и для
   * аддона, который сам умеет принимать фокус.
   */
  > *:focus-within {
    position: relative;
    z-index: 1;
  }
`;

export const Addon = styled.span<SizeProps>`
  ${({ theme, $size }) => {
    const { height, paddingInline, borderRadius, fontSize, lineHeight } =
      sizeConfig(theme)[$size];

    return css`
      box-sizing: border-box;
      display: flex;
      align-items: center;
      flex: none;
      height: ${height};
      padding-inline: ${paddingInline};
      font-size: ${fontSize};
      line-height: ${lineHeight};
      background-color: ${theme.semantic.field.bgPrefix};
      border: 1px solid ${theme.semantic.border.control};
      color: ${theme.semantic.text.secondary};
      white-space: nowrap;
      border-radius: ${borderRadius};
    `;
  }}
`;
